import React, { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Seo } from "./Seo";
import PageHero from "./UI/PageHero";
import SectionHeading from "./UI/SectionHeading";
import Breadcrumbs, { breadcrumbSchema } from "./UI/Breadcrumbs";
import Icon from "./UI/Icon";
import { SITE } from "../data/site";
import emailjs from "@emailjs/browser";

export default function Contact() {
  const formRef = useRef(null);
  const [status, setStatus] = useState("idle"); // idle | sending | success | error

  const handleSubmit = (event) => {
    event.preventDefault();
    setStatus("sending");
    emailjs
      .sendForm("service_r5bviyi", "template_fbd5858", formRef.current, {
        publicKey: "wz4LipO_3T-LIVb8l",
      })
      .then(
        () => setStatus("success"),
        (error) => {
          console.error("Contact form failed:", error?.status, error?.text);
          setStatus("error");
        }
      );
  };

  const crumbs = [
    { label: "Home", to: "/" },
    { label: "Contact" },
  ];

  const inputClass =
    "bg-linen w-full p-3 border border-hairline focus:border-oak text-ink placeholder:text-bark/50";

  return (
    <>
      <Seo
        title="Contact Cambridge Carpenter | Free Carpentry Quotes in Cambridge"
        description="Contact Cambridge Carpenter for fitted furniture, kitchens, decking and bespoke joinery across Cambridge. Call 07798 525115 or send a message for a free quote."
        canonical="https://www.cambridgecarpenter.co.uk/contact/"
        schema={breadcrumbSchema(crumbs)}
      />

      <PageHero
        eyebrow="Get in touch"
        title="Tell us about your project"
        intro="Friendly, local and happy to help, from a single door to a full room refit."
        crumbs={<Breadcrumbs items={crumbs} />}
      />

      <section className="max-w-site mx-auto px-5 sm:px-10 pt-20 pb-16 md:pt-28 md:pb-20">
        <div className="lg:grid lg:grid-cols-[1.4fr_1fr] lg:gap-16 items-start">
          <div className="mb-14 lg:mb-0">
            <SectionHeading
              eyebrow="Send a message"
              title="Request a free, no-obligation quote"
              intro="Fill in a few details and we’ll get back to you to arrange a visit. Prefer to talk? Give us a call."
            />
            {status === "success" && (
              <p role="status" className="bg-oak/10 border border-oak/30 text-ink px-4 py-3 mt-6 font-semibold">
                Thank you for your message, we’ll get back to you as soon as possible.
              </p>
            )}
            {status === "error" && (
              <p role="alert" className="bg-red-50 border border-red-300 text-ink px-4 py-3 mt-6">
                <span className="font-semibold">Sorry, your message couldn’t be sent.</span> Please call us on{" "}
                <a href={SITE.phoneHref} className="underline">{SITE.phone}</a> or email{" "}
                <a href={`mailto:${SITE.email}`} className="underline break-all">{SITE.email}</a>.
              </p>
            )}
            <form ref={formRef} onSubmit={handleSubmit} className="mt-8">
              <div className="mb-4 md:flex md:gap-5">
                <div className="flex-1 mb-4 md:mb-0">
                  <label htmlFor="name" className="block mb-2 text-sm font-medium">Name</label>
                  <input type="text" id="name" name="name" required className={inputClass} placeholder="Your name" />
                </div>
                <div className="flex-1">
                  <label htmlFor="telephone" className="block mb-2 text-sm font-medium">Phone number</label>
                  <input type="tel" id="telephone" name="telephone" required className={inputClass} placeholder="Your phone number" />
                </div>
              </div>
              <div className="mb-4">
                <label htmlFor="email" className="block mb-2 text-sm font-medium">Email</label>
                <input type="email" id="email" name="email" required className={inputClass} placeholder="Your email address" />
              </div>
              <div className="mb-4">
                <label htmlFor="subject" className="block mb-2 text-sm font-medium">Subject</label>
                <input type="text" id="subject" name="subject" className={inputClass} placeholder="Let us know how we can help you" />
              </div>
              <div className="mb-8">
                <label htmlFor="message" className="block mb-2 text-sm font-medium">Your message</label>
                <textarea id="message" name="message" rows="6" className={inputClass} placeholder="Tell us about your project..."></textarea>
              </div>
              <button type="submit" disabled={status === "sending"} className="btn-primary w-full md:w-auto disabled:opacity-60">
                {status === "sending" ? "Sending…" : "Send message"}
              </button>
            </form>
          </div>

          <aside className="bg-ink text-paper p-8 relative overflow-hidden">
            <div className="grain relative">
              <h2 className="font-display font-semibold text-2xl">Contact details</h2>
              <ul className="mt-6 space-y-6">
                <li className="flex items-start gap-3">
                  <Icon name="pin" className="w-5 h-5 text-brass shrink-0 mt-1" />
                  <span className="leading-7">
                    {SITE.address.street}<br />
                    {SITE.address.town}<br />
                    {SITE.address.postcode}
                  </span>
                </li>
                <li className="flex items-center gap-3">
                  <Icon name="phone" className="w-5 h-5 text-brass shrink-0" />
                  <a href={SITE.phoneHref} className="hover:text-brass transition-colors">{SITE.phone}</a>
                </li>
                <li className="flex items-center gap-3">
                  <Icon name="mail" className="w-5 h-5 text-brass shrink-0" />
                  <a href={`mailto:${SITE.email}`} className="hover:text-brass transition-colors break-all">{SITE.email}</a>
                </li>
              </ul>
              <div className="border-t border-white/10 mt-8 pt-6">
                <h3 className="font-display font-semibold text-lg">Areas we cover</h3>
                <p className="text-paper/75 leading-7 mt-2">
                  Based in Cambridge, we cover Cambridgeshire and the surrounding villages. If you’re
                  further afield, drop us a line and we’ll see how we can help.
                </p>
                <Link to="/carpentry-services" className="btn-outline-light w-full mt-6">
                  Browse our services
                </Link>
              </div>
            </div>
          </aside>
        </div>
      </section>
    </>
  );
}
